import { arrayMove } from '@dnd-kit/sortable';
import type { SalesLead, SalesLeadStatus } from '../../types/sales';

const PO = (l: SalesLead) => l.pipeline_order ?? 0;

export function sortLeadsByPipeline(a: SalesLead, b: SalesLead): number {
  const d = PO(a) - PO(b);
  if (d !== 0) return d;
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

/** New pipeline_order between optional neighbors (same status column). */
export function orderFromNeighbors(prevLead: SalesLead | null, nextLead: SalesLead | null): number {
  if (!prevLead && !nextLead) return 1000;
  if (!prevLead) return PO(nextLead!) - 1000;
  if (!nextLead) return PO(prevLead) + 1000;
  return (PO(prevLead) + PO(nextLead)) / 2;
}

export function computeKanbanPipelineOrder(
  leads: SalesLead[],
  leadId: string,
  targetStatus: SalesLeadStatus,
  overId: string,
  kanbanColumns: readonly SalesLeadStatus[],
): { newPipelineOrder: number; newStatus: SalesLeadStatus; changed: boolean } | null {
  const lead = leads.find((l) => l.id === leadId);
  if (!lead) return null;

  const columnWithoutDrag = leads
    .filter((l) => l.status === targetStatus && l.id !== leadId)
    .sort(sortLeadsByPipeline);

  let prevLead: SalesLead | null = null;
  let nextLead: SalesLead | null = null;

  if (kanbanColumns.includes(overId as SalesLeadStatus)) {
    if (columnWithoutDrag.length === 0) {
      prevLead = null;
      nextLead = null;
    } else {
      prevLead = columnWithoutDrag[columnWithoutDrag.length - 1];
      nextLead = null;
    }
  } else if (lead.status === targetStatus) {
    const fullSorted = leads.filter((l) => l.status === targetStatus).sort(sortLeadsByPipeline);
    const ids = fullSorted.map((l) => l.id);
    const oldIndex = ids.indexOf(leadId);
    const overIndex = ids.indexOf(overId);
    if (oldIndex === -1 || overIndex === -1) return null;
    if (oldIndex === overIndex) {
      return { newPipelineOrder: PO(lead), newStatus: targetStatus, changed: false };
    }
    const reorderedIds = arrayMove(ids, oldIndex, overIndex);
    const newIdx = reorderedIds.indexOf(leadId);
    const prevId = newIdx > 0 ? reorderedIds[newIdx - 1] : null;
    const nextId = newIdx < reorderedIds.length - 1 ? reorderedIds[newIdx + 1] : null;
    prevLead = prevId ? leads.find((l) => l.id === prevId) ?? null : null;
    nextLead = nextId ? leads.find((l) => l.id === nextId) ?? null : null;
  } else {
    const insertIndex = columnWithoutDrag.findIndex((l) => l.id === overId);
    const idx = insertIndex === -1 ? columnWithoutDrag.length : insertIndex;
    prevLead = idx > 0 ? columnWithoutDrag[idx - 1] : null;
    nextLead = idx < columnWithoutDrag.length ? columnWithoutDrag[idx] : null;
  }

  const newPipelineOrder = orderFromNeighbors(prevLead, nextLead);
  const statusChanged = lead.status !== targetStatus;
  const orderChanged = Math.abs(PO(lead) - newPipelineOrder) > 1e-6;
  const changed = statusChanged || orderChanged;

  return { newPipelineOrder, newStatus: targetStatus, changed };
}
